"use client";

import React from "react";
import { cn } from "@/lib/utils";
import {
  FormControl,
  FormControlElementProps,
  FormField as BaseFormField,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

type FormFieldProps = {
  label?: string;
  error?: string;
  helperText?: string;
  floatingLabel?: boolean;
  children: React.ReactNode;
  className?: string;
};

type FloatingControlProps = FormControlElementProps & {
  className?: string;
  placeholder?: string;
};

const FormField = ({
  label,
  error,
  helperText,
  floatingLabel,
  children,
  className,
}: FormFieldProps) => {
  const hasError = Boolean(error);
  const floatingContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [isFloatingActive, setIsFloatingActive] = React.useState(false);

  const childElement = React.isValidElement(children)
    ? (children as React.ReactElement<FloatingControlProps>)
    : null;

  const floatingControl =
    floatingLabel && childElement
      ? React.cloneElement(childElement, {
          placeholder: childElement.props.placeholder ?? " ",
        })
      : childElement;

  React.useEffect(() => {
    if (!floatingLabel || !floatingContainerRef.current) {
      setIsFloatingActive(false);
      return;
    }

    const control = floatingContainerRef.current.querySelector(
      "input, textarea, select"
    ) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;

    if (!control) {
      setIsFloatingActive(false);
      return;
    }

    const hasValue = () => control.value !== "";
    const syncState = () => {
      const isFocused = document.activeElement === control;
      setIsFloatingActive(isFocused || hasValue());
    };

    syncState();

    control.addEventListener("focus", syncState);
    control.addEventListener("blur", syncState);
    control.addEventListener("input", syncState);
    control.addEventListener("change", syncState);

    return () => {
      control.removeEventListener("focus", syncState);
      control.removeEventListener("blur", syncState);
      control.removeEventListener("input", syncState);
      control.removeEventListener("change", syncState);
    };
  }, [floatingLabel, children]);

  return (
    <BaseFormField className={className} hasError={hasError}>
      {floatingLabel && label && floatingControl ? (
        <div ref={floatingContainerRef} className="relative">
          <FormControl>
            {floatingControl as React.ReactElement<FormControlElementProps>}
          </FormControl>
          <FormLabel
            className={cn(
              "pointer-events-none absolute left-3  px-1 text-[var(--gray-500)] transition-all duration-150",
              isFloatingActive
                ? "top-0 -translate-y-1/2 text-xs text-[var(--blue-500)] bg-white rounded-full"
                : "top-1/2 -translate-y-1/2 text-sm"
            )}
          >
            {label}
          </FormLabel>
        </div>
      ) : (
        <>
          {label ? <FormLabel>{label}</FormLabel> : null}
          {childElement ? (
            <FormControl>
              {childElement as React.ReactElement<FormControlElementProps>}
            </FormControl>
          ) : (
            children
          )}
        </>
      )}
      <FormMessage error={error} helperText={helperText} />
    </BaseFormField>
  );
};

export default FormField;